import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiError } from '../../Documents/format';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT, EMPTY_FILTERS, SEARCH_DELAY_MS } from '../constants';
import { isCancelled, timesheetApi } from '../timesheetApi';
import { SortField, SortState, StatusFilter, TimesheetFilters, TimesheetPagination, TimesheetRow } from '../types';

/** Поля, по которым естественный порядок «от новых к старым» (первый клик по заголовку - по убыванию) */
const DESC_FIRST: SortField[] = ['date'];

/**
 * Список аптек со статусом табеля: поиск + статус + сортировка + пагинация.
 *  - поиск применяется с задержкой, устаревшие запросы отменяются;
 *  - при перезагрузке прежние строки остаются на экране (без «мигания»);
 *  - страница нумеруется с 1 (как на сервере).
 */
export function useTimesheetList(enabled: boolean) {
  const [filters, setFilters] = useState<TimesheetFilters>(EMPTY_FILTERS);
  const [searchApplied, setSearchApplied] = useState('');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [limit, setLimitState] = useState(DEFAULT_PAGE_SIZE);
  const [reloadToken, setReloadToken] = useState(0);

  const [rows, setRows] = useState<TimesheetRow[]>([]);
  const [pagination, setPagination] = useState<TimesheetPagination>({ total: 0, page: 1, limit: DEFAULT_PAGE_SIZE });
  const [folderConfigured, setFolderConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  // Поиск: отложенное применение, чтобы не слать запрос на каждый символ
  useEffect(() => {
    if (filters.search === searchApplied) return undefined;
    const timer = window.setTimeout(() => {
      setSearchApplied(filters.search);
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [filters.search, searchApplied]);

  const effective = useMemo<TimesheetFilters>(() => ({ ...filters, search: searchApplied }), [filters, searchApplied]);

  useEffect(() => {
    if (!enabled) return undefined;
    const current = ++seq.current;
    const controller = new AbortController();
    setLoading(true);

    timesheetApi
      .list(effective, sort, page, limit, controller.signal)
      .then(data => {
        if (current !== seq.current) return;
        setRows(data.rows);
        setPagination(data.pagination);
        setFolderConfigured(data.folderConfigured);
        setError(null);
      })
      .catch(err => {
        if (isCancelled(err) || current !== seq.current) return;
        setError(apiError(err, 'Не удалось загрузить список аптек'));
      })
      .finally(() => {
        if (current === seq.current) setLoading(false);
      });

    return () => controller.abort();
  }, [enabled, effective, sort, page, limit, reloadToken]);

  const setSearch = useCallback((search: string) => setFilters(prev => ({ ...prev, search })), []);

  const setStatus = useCallback((status: StatusFilter) => {
    setFilters(prev => ({ ...prev, status }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setSearchApplied('');
    setPage(1);
  }, []);

  const toggleSort = useCallback((key: SortField) => {
    setSort(prev => (prev.sortBy === key
      ? { sortBy: key, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' }
      : { sortBy: key, sortOrder: DESC_FIRST.indexOf(key) !== -1 ? 'desc' : 'asc' }));
    setPage(1);
  }, []);

  const setLimit = useCallback((value: number) => {
    setLimitState(value);
    setPage(1);
  }, []);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  return {
    filters, setSearch, setStatus, resetFilters,
    sort, toggleSort,
    page, setPage, limit, setLimit,
    rows, pagination, folderConfigured, loading, error, reload
  };
}
